// Pokročilý notifikačný systém pre TaskKrow - Production ready komponent

import React, { useEffect } from 'react';
import { Bell, Check, X, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest, getQueryFn } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';

interface NotificationCounts {
  overdue: number;
  today: number;
  upcoming: number;
  badge: number;
}

interface Notification {
  id: string | number;
  title: string;
  dueDate: string;
  category: 'overdue' | 'today' | 'upcoming';
  projectId?: string;
  dismissedUntil?: string | null;
  seen: boolean;
}

interface NotificationsResponse {
  counts: NotificationCounts;
  items: Notification[];
  pagination: {
    page: number;
    limit: number;
    total: number;
  };
}

export function NotificationBell() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [isOpen, setIsOpen] = React.useState(false);

  // Načítanie počtu notifikácií pre badge
  const { data: counts, error: countsError } = useQuery<NotificationCounts>({
    queryKey: ['/api/notifications/counts'],
    refetchInterval: 30000, // Refresh každých 30 sekúnd
    refetchIntervalInBackground: false,
    retry: (failureCount, error: any) => {
      // Ak dostaneme 429 (rate limit), nepokúšaj sa znovu
      if (error?.status === 429) {
        toast({
          title: "Informácia",
          description: "Skúste znova o chvíľu",
          variant: "default"
        });
        return false;
      }
      return failureCount < 3;
    }
  });

  // Načítanie detailných notifikácií - nová štruktúra API
  const { data: notificationsData, isLoading, error: notificationsError } = useQuery<{
    notifications: any[];
    pagination: any;
    counts: {
      overdue: number;
      today: number;
      upcoming: number;
      badge: number;
    };
  }>({
    queryKey: ['/api/notifications'],
    enabled: isOpen, // Načítaj len keď je panel otvorený
    refetchInterval: isOpen ? 15000 : false, // Refresh každých 15 sekúnd keď je otvorený
    staleTime: 0, // Vždy refetch pri otvorení
    retry: (failureCount, error: any) => {
      // Ak dostaneme 429 (rate limit), nepokúšaj sa znovu
      if (error?.status === 429) {
        toast({
          title: "Informácia",
          description: "Skúste znova o chvíľu",
          variant: "default"
        });
        return false;
      }
      return failureCount < 3;
    }
  });

  const badgeCount = Number(counts?.badge) || 0;

  // Označenie notifikácií ako videných
  const markSeenMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest('POST', '/api/notifications/mark-seen');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/notifications/counts'] });
      queryClient.invalidateQueries({ queryKey: ['/api/notifications'] });
    }
  });

  // Snooze notifikácie
  const snoozeMutation = useMutation({
    mutationFn: async ({ id, hours }: { id: string | number; hours: number }) => {
      const res = await apiRequest('POST', `/api/notifications/${id}/snooze`, { hours });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/notifications/counts'] });
      queryClient.invalidateQueries({ queryKey: ['/api/notifications'] });
      toast({
        title: "Notifikácia odložená",
        description: "Notifikácia bola úspešne odložená"
      });
    }
  });

  // Synchronizácia notifikácií
  const syncMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest('POST', '/api/notifications/sync');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/notifications/counts'] });
      queryClient.invalidateQueries({ queryKey: ['/api/notifications'] });
    }
  });

  // Automatické označenie ako seen pri otvorení panelu - deaktivované kvôli zobrazeniu
  // useEffect(() => {
  //   if (isOpen && badgeCount > 0) {
  //     const timer = setTimeout(() => {
  //       markSeenMutation.mutate();
  //     }, 500); // Debounce 500ms
  //     return () => clearTimeout(timer);
  //   }
  // }, [isOpen, badgeCount]);

  const getCategoryIcon = (category: Notification['category']) => {
    switch (category) {
      case 'overdue':
        return <X className="h-4 w-4 text-red-500" />;
      case 'today':
        return <Clock className="h-4 w-4 text-orange-500" />;
      case 'upcoming':
        return <Bell className="h-4 w-4 text-blue-500" />;
      default:
        return <Bell className="h-4 w-4 text-gray-500" />;
    }
  };

  const getCategoryLabel = (category: Notification['category']) => {
    switch (category) {
      case 'overdue':
        return 'Po termíne';
      case 'today':
        return 'Dnes';
      case 'upcoming':
        return 'Nadchádzajúce';
      default:
        return 'Notifikácie';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('sk-SK', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };



  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="relative" data-onboarding="notification-bell">
          <Bell className="h-5 w-5" />
          {badgeCount > 0 && (
            <Badge 
              variant="destructive" 
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
            >
              {badgeCount > 99 ? '99+' : badgeCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      
      <PopoverContent className="w-96 p-0 z-[60]" align="end" side="top">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="font-semibold">Notifikácie</h3>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => syncMutation.mutate()}
              disabled={syncMutation.isPending}
            >
              Obnoviť
            </Button>
            {counts && (Number(counts.overdue) > 0 || Number(counts.today) > 0) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => markSeenMutation.mutate()}
                disabled={markSeenMutation.isPending}
              >
                Označiť všetko
              </Button>
            )}
          </div>
        </div>

        <ScrollArea className="h-80">
          {isLoading ? (
            <div className="p-4 text-center text-muted-foreground">
              Načítavam notifikácie...
            </div>
          ) : (!notificationsData?.counts?.overdue && !notificationsData?.counts?.today && !notificationsData?.counts?.upcoming) ? (
            <div className="p-4 text-center text-muted-foreground">
              Žiadne notifikácie
            </div>
          ) : (
            <div className="divide-y">
              {/* Overdue tasks */}
              {notificationsData?.notifications?.filter(task => task.type === 'overdue')?.map((task) => (
                <div
                  key={`overdue-${task.id}`}
                  className="p-4 hover:bg-muted/50 cursor-pointer bg-red-50 hover:bg-red-100 dark:bg-red-950/20"
                  onClick={() => {
                    setIsOpen(false);
                    window.location.href = `/tasks?highlight=${task.id}`;
                  }}
                >
                  <div className="flex items-start gap-3">
                    <X className="h-4 w-4 text-red-500 mt-0.5" />
                    <div className="flex-1 space-y-1">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-red-700 dark:text-red-300">
                          {task.title}
                        </p>
                        <span className="text-xs text-red-600 dark:text-red-400 font-medium">
                          Po termíne
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Klient: {task.client?.name || 'Bez klienta'}
                      </p>
                      <p className="text-xs text-red-600 dark:text-red-400">
                        Termín: {new Date(task.dueDate).toLocaleDateString('sk-SK')}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
              
              {/* Today's tasks */}
              {notificationsData?.notifications?.filter(task => task.type === 'today')?.map((task) => (
                <div
                  key={`today-${task.id}`}
                  className="p-4 hover:bg-muted/50 cursor-pointer bg-orange-50 hover:bg-orange-100 dark:bg-orange-950/20"
                  onClick={() => {
                    setIsOpen(false);
                    window.location.href = `/tasks?highlight=${task.id}`;
                  }}
                >
                  <div className="flex items-start gap-3">
                    <Clock className="h-4 w-4 text-orange-500 mt-0.5" />
                    <div className="flex-1 space-y-1">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-orange-700 dark:text-orange-300">
                          {task.title}
                        </p>
                        <span className="text-xs text-orange-600 dark:text-orange-400 font-medium">
                          Dnes
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Klient: {task.client?.name || 'Bez klienta'}
                      </p>
                      <p className="text-xs text-orange-600 dark:text-orange-400">
                        Termín: {new Date(task.dueDate).toLocaleDateString('sk-SK')}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
              
              {/* Upcoming tasks */}
              {notificationsData?.notifications?.filter(task => task.type === 'upcoming')?.map((task) => (
                <div
                  key={`upcoming-${task.id}`}
                  className="p-4 hover:bg-muted/50 cursor-pointer bg-blue-50 hover:bg-blue-100 dark:bg-blue-950/20"
                  onClick={() => {
                    setIsOpen(false);
                    window.location.href = `/tasks?highlight=${task.id}`;
                  }}
                >
                  <div className="flex items-start gap-3">
                    <Bell className="h-4 w-4 text-blue-500 mt-0.5" />
                    <div className="flex-1 space-y-1">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-blue-700 dark:text-blue-300">
                          {task.title}
                        </p>
                        <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">
                          Nadchádzajúce
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Klient: {task.client?.name || 'Bez klienta'}
                      </p>
                      <p className="text-xs text-blue-600 dark:text-blue-400">
                        Termín: {new Date(task.dueDate).toLocaleDateString('sk-SK')}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>

        {notificationsData && (notificationsData.counts?.overdue > 0 || notificationsData.counts?.today > 0 || notificationsData.counts?.upcoming > 0) && (
          <>
            <Separator />
            <div className="p-2">
              <Button
                variant="ghost"
                size="sm"
                className="w-full"
                onClick={() => {
                  setIsOpen(false);
                  window.location.href = '/tasks';
                }}
              >
                Zobraziť všetky notifikácie
              </Button>
            </div>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}